'use no memo';
import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {CUSTOM_CONTROLS_WRAPPER} from '@PosterWhiteboard/libraries/custom-item-controls.library';
import {CustomOutlineBoxes} from '@Components/poster-editor/components/custom-item-controls/components/custom-outline-boxes/custom-outline-boxes';
import {SingleItemCustomControls} from '@Components/poster-editor/components/custom-item-controls/components/single-item-custom-controls/single-item-custom-controls';
import {ActiveSelectionCustomControls} from '@Components/poster-editor/components/custom-item-controls/components/active-selection-custom-controls/active-selection-custom-controls';
import {ItemLoadingIndicators} from '@Components/poster-whiteboard/components/item-loading-indicator';
import {CropHandlesOverlay} from '@Components/poster-editor/components/crop-handles-overlay/crop-handles-overlay';
import {PagePresenceLayer} from '@Components/poster-editor/components/page-presence-layer/page-presence-layer';
import type {CustomItemControlsWrapperProps} from '@Components/poster-editor/components/custom-item-controls/components/custom-item-controls-wrapper/custom-item-controls-wrapper.types';
import {CANVAS_BACKDROP_CLASS, PAGE_CONTAINER_CLASS} from '@PosterWhiteboard/page/page.types';
import {WhiteboardDomElement} from '@PosterWhiteboard/poster/whiteboard-dom.types';
import {useShouldHideControlsWhenItemAnimating, useShouldHideHoveredItemControlsWhenAnimating} from '@Hooks/poster-editor/useShouldHideControlsWhenItemAnimating';
import {useAppSelector} from '@/hooks';

export function CustomItemControlsWrapper(props: CustomItemControlsWrapperProps): ReactElement | null {
  const hideActiveItemControlsDueToAnimation = useShouldHideControlsWhenItemAnimating(props.pageHashedId);
  const hideHoveredItemControlsDueToAnimation = useShouldHideHoveredItemControlsWhenAnimating(props.pageHashedId);
  const hideSelectedOrHoveredItemControlsDueToAnimation = hideActiveItemControlsDueToAnimation || hideHoveredItemControlsDueToAnimation;
  const hideControlsWhileEditing = useAppSelector((state) => {
    const activeItemIds = state.posterEditor.activeItemIds[props.pageHashedId];
    return activeItemIds?.some((itemId) => state.posterEditor.shouldHideControls[itemId]) ?? false;
  });
  const isCropModeActive = useAppSelector((state) => {
    return state.mediaCropMode.isActive;
  });
  const cropModePageHashedId = useAppSelector((state) => {
    return state.mediaCropMode.pageHashedId;
  });
  const isCutoutRefineModeActive = useAppSelector((state) => {
    return state.cutoutRefineMode.isActive;
  });
  const isTextOnPathModeActive = useAppSelector((state) => {
    return state.textOnPathMode.isActive;
  });
  const [backdropElement, setBackdropElement] = useState<HTMLElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const updatePositionRef = useRef<(() => void) | undefined>(undefined);

  const onWrapperMount = (node: HTMLDivElement | null): void => {
    wrapperRef.current = node;
    if (node) {
      updatePositionRef.current?.();
    }
  };

  useEffect(() => {
    const backdropSelector = `.${CANVAS_BACKDROP_CLASS}#${CSS.escape(props.pageHashedId)}`;
    const rootToObserve = window.posterEditor?.elements[WhiteboardDomElement.WHITEBOARD_CONTAINER]?.get?.(0) ?? document.body;

    setBackdropElement(document.querySelector<HTMLElement>(backdropSelector));

    const observer = new MutationObserver(() => {
      setBackdropElement(document.querySelector<HTMLElement>(backdropSelector));
    });

    observer.observe(rootToObserve, {childList: true, subtree: true});

    return (): void => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!backdropElement) {
      return;
    }

    const container = window.posterEditor?.elements[WhiteboardDomElement.WHITEBOARD_CONTAINER]?.get?.(0);
    if (!container) {
      return;
    }

    const updatePosition = (): void => {
      const wrapper = wrapperRef.current;
      if (!wrapper) {
        return;
      }
      const backdropRect = backdropElement.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      wrapper.style.top = `${backdropRect.top - containerRect.top + container.scrollTop}px`;
      wrapper.style.left = `${backdropRect.left - containerRect.left + container.scrollLeft}px`;
      // The controls are laid out in the page's own pixels; mirror the page container's CSS scale
      // (inactive pages are scaled down) so they stay glued to the items on screen.
      const backdropLayoutWidth = parseFloat(window.getComputedStyle(backdropElement).width);
      const pageCssScale = backdropLayoutWidth > 0 ? backdropRect.width / backdropLayoutWidth : 1;
      wrapper.style.transformOrigin = 'top left';
      wrapper.style.transform = Math.abs(pageCssScale - 1) < 0.0005 ? '' : `scale(${pageCssScale})`;
    };

    updatePositionRef.current = updatePosition;
    updatePosition();

    const resizeObserver = new ResizeObserver(updatePosition);
    window.addEventListener('resize', updatePosition);
    container.addEventListener('scroll', updatePosition);
    resizeObserver.observe(backdropElement);
    resizeObserver.observe(container);

    // Follow the page container's transform transition frame by frame, then settle on its end value.
    const pageContainer = backdropElement.closest<HTMLElement>(`.${PAGE_CONTAINER_CLASS}`);
    let transitionFrameId: number | undefined;
    const stopFollowingTransition = (): void => {
      if (transitionFrameId !== undefined) {
        window.cancelAnimationFrame(transitionFrameId);
        transitionFrameId = undefined;
      }
    };
    const isPageContainerTransformTransition = (event: TransitionEvent): boolean => {
      return event.target === pageContainer && event.propertyName === 'transform';
    };
    const onTransitionStart = (event: TransitionEvent): void => {
      if (!isPageContainerTransformTransition(event)) {
        return;
      }
      stopFollowingTransition();
      const followTransition = (): void => {
        updatePosition();
        transitionFrameId = window.requestAnimationFrame(followTransition);
      };
      transitionFrameId = window.requestAnimationFrame(followTransition);
    };
    const onTransitionSettled = (event: TransitionEvent): void => {
      if (!isPageContainerTransformTransition(event)) {
        return;
      }
      stopFollowingTransition();
      updatePosition();
    };
    pageContainer?.addEventListener('transitionstart', onTransitionStart);
    pageContainer?.addEventListener('transitionend', onTransitionSettled);
    pageContainer?.addEventListener('transitioncancel', onTransitionSettled);

    return (): void => {
      updatePositionRef.current = undefined;
      stopFollowingTransition();
      window.removeEventListener('resize', updatePosition);
      container.removeEventListener('scroll', updatePosition);
      pageContainer?.removeEventListener('transitionstart', onTransitionStart);
      pageContainer?.removeEventListener('transitionend', onTransitionSettled);
      pageContainer?.removeEventListener('transitioncancel', onTransitionSettled);
      resizeObserver.disconnect();
    };
  }, [backdropElement]);

  if (!backdropElement) {
    return null;
  }

  const page = window.posterEditor?.whiteboard?.pages?.getPage(props.pageHashedId);
  if (!page) {
    return null;
  }

  const container = window.posterEditor?.elements[WhiteboardDomElement.WHITEBOARD_CONTAINER]?.get?.(0);
  if (!container) {
    return null;
  }

  /**
   * Crop mode owns the editor: the frame is drawn on the page being cropped and every other page
   * shows no controls, so nothing there can be moved while the crop is open.
   */
  const getCropModeControls = (): ReactElement | null => {
    if (cropModePageHashedId !== props.pageHashedId) {
      return null;
    }
    return <CropHandlesOverlay pageHashedId={props.pageHashedId} />;
  };

  const getSelectionControls = (): ReactElement | null => {
    if (isCropModeActive) {
      return getCropModeControls();
    }
    // Refine keeps the item selected but frozen: its outline shows, with no move, resize or rotate handles.
    if (isCutoutRefineModeActive) {
      return <CustomOutlineBoxes pageHashedId={props.pageHashedId} />;
    }
    // Drawing a text path: the pencil owns the canvas, so no selection and no handles until the drawing is finished.
    if (hideControlsWhileEditing || isTextOnPathModeActive) {
      return null;
    }
    return (
      <>
        <CustomOutlineBoxes pageHashedId={props.pageHashedId} />
        <SingleItemCustomControls pageHashedId={props.pageHashedId} />
        <ActiveSelectionCustomControls pageHashedId={props.pageHashedId} />
      </>
    );
  };

  /** The other editors' presence stays while this editor's own controls hide for an animation. */
  const getOwnControls = (): ReactElement | null => {
    if (hideSelectedOrHoveredItemControlsDueToAnimation) {
      return null;
    }
    return (
      <>
        {getSelectionControls()}
        <ItemLoadingIndicators pageHashedId={props.pageHashedId} />
      </>
    );
  };

  return createPortal(
    <div ref={onWrapperMount} style={{position: 'absolute', top: 0, left: 0, zIndex: 1, contain: 'layout'}} className={CUSTOM_CONTROLS_WRAPPER} id={props.pageHashedId}>
      <PagePresenceLayer pageId={props.pageHashedId} />
      {getOwnControls()}
    </div>,
    container
  );
}
